/*
 * QuestionarioReportDialog — relatório do Questionário Psicossocial com
 * gráficos de pizza (% de cada resposta) por pergunta + filtro por posto.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from "recharts";
import { API } from "../App";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "./ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "./ui/select";
import { Loader2, Download } from "lucide-react";
import { Button } from "./ui/button";
import { toast } from "sonner";

const COLORS = ["#dc2626", "#9333ea", "#f59e0b", "#84cc16", "#10b981"];
const SHORT = ["Sempre", "Frequentemente", "Às vezes", "Raramente", "Nunca"];
const RADIAN = Math.PI / 180;

const renderSliceLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent }) => {
  if (percent < 0.05) return null;
  const r = innerRadius + (outerRadius - innerRadius) * 0.55;
  const x = cx + r * Math.cos(-midAngle * RADIAN);
  const y = cy + r * Math.sin(-midAngle * RADIAN);
  return (
    <text x={x} y={y} fill="#fff" fontSize={13} fontWeight={700}
          textAnchor="middle" dominantBaseline="central"
          style={{ textShadow: "0 1px 2px rgba(0,0,0,0.45)" }}>
      {(percent * 100).toFixed(0)}%
    </text>
  );
};

export const QuestionarioReportDialog = ({ open, onClose, list, postoFixo, year }) => {
  const [report, setReport] = useState(null);
  const [posto, setPosto] = useState("__all__");
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  const exportPdf = async () => {
    setExporting(true);
    try {
      const r = await axios.get(`${API}/admin/questionario/posto-report.pdf`, {
        params: { posto: postoFixo, year },
        responseType: "blob",
        timeout: 120000,
      });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `questionario_${postoFixo.replace(/[^A-Za-z0-9._-]+/g, "_")}_${year}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("PDF do posto exportado!");
    } catch (e) {
      toast.error("Erro ao exportar PDF");
    } finally {
      setExporting(false);
    }
  };

  useEffect(() => {
    if (!open || (!list && !postoFixo)) return;
    setLoading(true);
    const req = postoFixo
      ? axios.get(`${API}/admin/questionario/posto-report`, {
          params: { posto: postoFixo, year },
        })
      : axios.get(`${API}/admin/questionario/lists/${list.id}/report`, {
          params: posto !== "__all__" ? { posto } : {},
        });
    req
      .then((r) => setReport(r.data))
      .catch(() => setReport(null))
      .finally(() => setLoading(false));
  }, [open, list, posto, postoFixo, year]);

  useEffect(() => { if (!open) { setReport(null); setPosto("__all__"); } }, [open]);

  const grouped = [];
  if (report) {
    let last = null;
    report.questions.forEach((q) => {
      const key = `${q.dimensao} — ${q.fator}`;
      if (key !== last) {
        grouped.push({ key, items: [] });
        last = key;
      }
      grouped[grouped.length - 1].items.push(q);
    });
  }

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent aria-describedby={undefined} className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="questionario-report-dialog">
        <DialogHeader>
          <DialogTitle>
            Relatório — Questionário Psicossocial
            {postoFixo ? ` · ${postoFixo} (${year})` : ""}
          </DialogTitle>
        </DialogHeader>
        {loading && (
          <div className="flex justify-center py-10">
            <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
          </div>
        )}
        {!loading && report && (
          <div className="space-y-5">
            <div className="flex flex-wrap items-center gap-3">
              <div className="text-sm text-slate-600" data-testid="questionario-report-stats">
                <span className="font-semibold text-slate-900">{report.respondidos}</span>
                {" "}de {report.participantes} responderam o questionário ·{" "}
                <span className="font-semibold text-slate-900">{report.total_respostas}</span>
                {" "}respostas no filtro
                {report.assinados != null && (
                  <>
                    {" "}·{" "}
                    <span className="font-semibold text-emerald-700">{report.assinados}</span>
                    {" "}assinaram a lista de presença
                  </>
                )}
              </div>
              {postoFixo && (
                <Button
                  size="sm"
                  onClick={exportPdf}
                  disabled={exporting || report.total_respostas === 0}
                  className="ml-auto bg-emerald-600 hover:bg-emerald-700 text-white"
                  data-testid="questionario-export-pdf-btn"
                >
                  {exporting ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Download className="w-4 h-4 mr-2" />
                  )}
                  Exportar PDF
                </Button>
              )}
              <div className={`ml-auto w-64 ${postoFixo ? "hidden" : ""}`}>
                <Select value={posto} onValueChange={setPosto}>
                  <SelectTrigger data-testid="questionario-report-posto-select">
                    <SelectValue placeholder="Todos os postos" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__all__">Todos os postos</SelectItem>
                    {report.postos.map((p) => (
                      <SelectItem key={p} value={p}>{p}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex flex-wrap gap-3 text-[11px]">
              {SHORT.map((s, i) => (
                <span key={s} className="inline-flex items-center gap-1.5">
                  <span className="w-3 h-3 rounded-sm" style={{ background: COLORS[i] }} />
                  {i + 1} - {s}
                </span>
              ))}
            </div>
            {report.total_respostas === 0 && (
              <p className="text-sm text-slate-500 text-center py-6">
                Nenhuma resposta ainda para este filtro.
              </p>
            )}
            {grouped.map((g) => (
              <div key={g.key} className="space-y-3">
                <p className="text-xs font-bold text-amber-900 bg-amber-50 border border-amber-100 rounded px-3 py-1.5">
                  {g.key}
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {g.items.map((q) => {
                    const data = SHORT.map((s, i) => ({
                      name: `${i + 1} - ${s}`,
                      value: q.counts[String(i + 1)],
                    })).filter((d) => d.value > 0);
                    return (
                      <div key={q.id} className="border border-slate-100 rounded-lg p-3" data-testid={`questionario-report-${q.id}`}>
                        <p className="text-xs font-medium text-slate-800 min-h-[32px]">{q.texto}</p>
                        {q.total === 0 ? (
                          <p className="text-[11px] text-slate-400 py-8 text-center">Sem respostas</p>
                        ) : (
                          <div className="h-44">
                            <ResponsiveContainer width="100%" height="100%">
                              <PieChart>
                                <Pie data={data} dataKey="value" nameKey="name"
                                     cx="50%" cy="50%" outerRadius={72}
                                     startAngle={90} endAngle={-270}
                                     isAnimationActive={false}
                                     labelLine={false} label={renderSliceLabel}>
                                  {data.map((d) => (
                                    <Cell key={d.name}
                                          fill={COLORS[parseInt(d.name[0], 10) - 1]} />
                                  ))}
                                </Pie>
                                <Tooltip formatter={(v, n) => [`${v} resposta(s)`, n]} />
                              </PieChart>
                            </ResponsiveContainer>
                          </div>
                        )}
                        <div className="flex justify-between text-[10px] text-slate-500 mt-1">
                          {SHORT.map((s, i) => (
                            <span key={s}>{i + 1}: {q.pct[String(i + 1)]}%</span>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
